/**
 * The two things the native shell hands the app: opened URLs, and Android's
 * hardware back button. Both no-ops on the web, where the bridge has neither.
 *
 * **A link navigates; it never writes** (W-10, SPEC §15). The URL goes through
 * `parseDeepLink`, which knows the allowed navigation shapes and refuses the rest, and the
 * result is a route plus a query string for the screen to *read* — the numpad
 * prefills, the pairing sheet fills in a code. A link the parser refuses is
 * reported and goes nowhere.
 *
 * **Back** closes an open sheet first, then walks the WebView's history; at
 * the root, where there is nothing left to walk, it leaves the app — what
 * every Android app does (`backAction`). A sheet is closed by the Escape it
 * already answers (W-8), so every sheet obeys Back without knowing about it.
 */

import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { backAction } from "@/native/back_action";
import { parseDeepLink } from "@/native/deep_links";
import { bridge } from "@/native/impl";

export function useNativeShell(): void {
  const navigate = useNavigate();

  useEffect(
    () =>
      bridge.onDeepLink((url) => {
        const route = parseDeepLink(url);
        if (route === null) {
          // Reported, not swallowed (INV-7): a link this app does not route.
          console.warn("Ignored a link this app does not open:", url);
          return;
        }
        void navigate(`${route.path}${route.search}`);
      }),
    [navigate],
  );

  useEffect(
    () =>
      bridge.onBackButton((canGoBack) => {
        const action = backAction({
          canGoBack,
          pathname: window.location.pathname,
          dialogOpen: document.querySelector('[role="dialog"]') !== null,
        });
        const goBack = (next: "back" | "exit"): void => {
          if (next === "back") void navigate(-1);
          else void bridge.exitApp();
        };
        if (action !== "dismiss") {
          goBack(action);
          return;
        }
        const dialogs = document.querySelectorAll<HTMLElement>('[role="dialog"]');
        const dialog = dialogs[dialogs.length - 1];
        // Dispatched where focus is, so it bubbles to listeners on the
        // document and the window alike.
        (document.activeElement ?? document.body).dispatchEvent(
          new KeyboardEvent("keydown", { key: "Escape", bubbles: true }),
        );
        // A dismissed sheet stays mounted during its exit animation. Only a
        // dialog that remains open after Escape lets Back continue through history.
        requestAnimationFrame(() => {
          if (!dialog || !dialog.isConnected || dialog.dataset.phase === "exit") return;
          const next = backAction({ canGoBack, pathname: window.location.pathname, dialogOpen: false });
          goBack(next === "back" ? "back" : "exit");
        });
      }),
    [navigate],
  );
}
